import React, { useEffect, useRef, useState } from 'react'

/*
 * ContactTeamModal
 *
 * Mirrors the "Connect with our team" flow seen on
 * cadabamshospitals.com: the visitor taps the phone pill in the
 * header (or the floating phone button), we collect their callback
 * number so support can ring back if the call drops, then we trigger
 * the dial via a `tel:` anchor.
 *
 * The component is self-contained (no router, no services) so it can
 * be dropped into both the marketing site and the mentor landing
 * page. Styles are namespaced `ct-*` and live in AumhumSite.css.
 */

const COUNTRY_CODE = '+91'
const MIN_DIGITS = 7
const MAX_DIGITS = 15
const CALLBACK_LS_KEY = 'aumhum:callbackPhone'

export default function ContactTeamModal({ open, onClose, contact }) {
  const [phone, setPhone] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const inputRef = useRef(null)

  // Reset state + lock body scroll whenever the modal opens.
  useEffect(() => {
    if (!open) return undefined
    setError('')
    setSubmitting(false)
    setPhone(() => {
      try {
        const saved = localStorage.getItem(CALLBACK_LS_KEY)
        if (saved && saved.startsWith(COUNTRY_CODE)) {
          return saved.slice(COUNTRY_CODE.length).replace(/[^0-9]/g, '')
        }
      } catch (_e) {
        // access may throw in private mode — ignore
      }
      return ''
    })

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    document.addEventListener('keydown', onKey)
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 40)

    return () => {
      document.body.style.overflow = prevOverflow
      document.removeEventListener('keydown', onKey)
      window.clearTimeout(focusTimer)
    }
  }, [open, onClose])

  if (!open) return null

  const digits = phone.replace(/[^0-9]/g, '')
  const canContinue = digits.length >= MIN_DIGITS && !submitting

  const handleContinue = () => {
    if (digits.length < MIN_DIGITS) {
      setError('Please enter a valid phone number.')
      return
    }
    setError('')
    setSubmitting(true)

    // Persist locally so we can pre-fill next time. Fire-and-forget;
    // any storage exception (Safari private mode etc.) is harmless.
    try {
      localStorage.setItem(CALLBACK_LS_KEY, `${COUNTRY_CODE}${digits}`)
    } catch (_e) {
      /* ignore */
    }

    // Trigger the device's dialer. We use an <a> click rather than
    // `window.location.href = …` because some browsers (notably iOS
    // Safari) handle anchor-initiated tel: links more reliably when
    // the page is inside an iframe or an AMP wrapper.
    const anchor = document.createElement('a')
    anchor.href = contact?.telHref || 'tel:+916360903465'
    anchor.rel = 'noopener noreferrer'
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)

    // Close after a short tick so the native dialer has a chance to
    // pick up the intent before the modal unmounts.
    window.setTimeout(() => {
      setSubmitting(false)
      onClose?.()
    }, 300)
  }

  return (
    <div
      className="ct-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ct-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.()
      }}
    >
      <div className="ct-modal">
        <button
          type="button"
          className="ct-modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round"
               strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="18" x2="18" y2="6" />
          </svg>
        </button>

        <h2 id="ct-modal-title" className="ct-modal-title">
          Connect with our team
        </h2>
        <p className="ct-modal-sub">
          Share your number so we can reach you if the call drops. Your
          details stay private.
        </p>

        <label className="ct-modal-label" htmlFor="ct-modal-phone">
          Phone number
        </label>
        <div className={`ct-phone-row${error ? ' ct-phone-row--error' : ''}`}>
          <span className="ct-phone-cc" aria-hidden="true">{COUNTRY_CODE}</span>
          <input
            id="ct-modal-phone"
            ref={inputRef}
            className="ct-phone-input"
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            placeholder="98765 43210"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value)
              if (error) setError('')
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleContinue()
              }
            }}
            maxLength={MAX_DIGITS + 4 /* allow separators while typing */}
            aria-invalid={error ? 'true' : 'false'}
          />
        </div>
        {error ? <div className="ct-modal-error" role="alert">{error}</div> : null}

        <button
          type="button"
          className="ct-continue-btn"
          onClick={handleContinue}
          disabled={!canContinue}
          aria-disabled={!canContinue}
        >
          {submitting ? 'Connecting…' : 'Continue & Call'}
        </button>

        <p className="ct-modal-fine">
          By continuing you agree to be contacted by AUMHUM.
        </p>
      </div>
    </div>
  )
}
